import React, { useContext, useEffect, useMemo, useState } from 'react';
import { get } from 'lodash';
import styled from '@emotion/styled';
import { Box, Grid, MenuItem, Skeleton, Typography, lighten } from '@mui/material';
import { ConfigContext } from '@/contextAPI/configContext';
import { selectActiveTitle, selectActiveVersion, selectTitle, selectTitleLoading } from '@/redux/selectors/titleSelectors';
import { selectBaseLanguages } from '@/redux/selectors/constantsSelectors';
import { LabelValue, Spinner } from '@/components/_common';
import SchemaForm from '@/components/_common/SchemaForm/SchemaForm';
import axios from '@/_utils/axios';
import DropdownSelect from '@/components/_common/Form/DropdownSelect';
import { RJSFSchema } from '@rjsf/utils';
import CSVLanguages from '@/components/_common/CSVLanguages/CSVLanguages';
import { useAppSelector } from '@/redux/hooks';
import SizePreservingLoader from '@/components/_common/SizePreservingLoader/SizePreservingLoader';
import { isActiveVersionAVersion } from '@/redux/slices/slice_types';
import TitleDetailMetadataSchema from './TitleDetailMetadataSchema';
// import { FLAG_ICONS } from '@/constants/constants';

interface TitleMetadata {
  metadata_values: Record<string, any>;
  metadata_schema: RJSFSchema;
}

/**
 * Returns the most prioritized language from the given list of languages
 * based on a priority list. If none of the priority languages are found,
 * it returns the first language from the provided list.
 *
 * @param {string[]} languages - An array of available languages.
 * @param {string[]} langsPriority - An array of prioritized languages in order of preference.
 * @returns {string} - The most prioritized language, or the first language from the input list if no priority match is found.
 */
const getPriorityLanguage = (languages: string[], langsPriority: string[]) => {
  const langSet = new Set(Array.from(languages).map((lang) => lang.replace(/-/g, '_')));
  const lang = langsPriority.find((language) => langSet.has(language)) || languages[0];
  return languages[0].indexOf('-') > -1 ? lang.replace(/_/g, '-') : lang;
};

const TitleDetailMetadata = ({ disableDropdownOffset = false }: { disableDropdownOffset?: boolean }) => {
  const title = useAppSelector(selectActiveTitle);
  const parentTitle = useAppSelector(selectTitle);
  const customFields = useContext(ConfigContext)?.titleConfig?.metadata;
  const isLoading = useAppSelector(selectTitleLoading);
  const activeVersion = useAppSelector(selectActiveVersion);
  const baseLanguages = useAppSelector(selectBaseLanguages);
  const languagesPriority = useMemo(() => (baseLanguages ? baseLanguages.map((lang) => lang.code) : []), [baseLanguages]);
  const [fetchingTitleMetadata, setFetchingTitleMetadata] = useState(false);
  const [titleMetadata, setTitleMetadata] = useState<TitleMetadata | null>(null);
  const [versionMetadata, setVersionMetadata] = useState<TitleMetadata | null>(null);
  const [selectedLanguage, setSelectedLanguage] = useState(title?.metadata_languages?.[0] || '');

  const fetchMetadata = (titleId: string | number, language: string): Promise<TitleMetadata | null> => {
    setFetchingTitleMetadata(true);
    return axios
      .get(`/inventory/titles/${titleId}/metadata/${language}/`)
      .then((res) => {
        if (!res.data) {
          throw new Error('No metadata found');
        } else {
          return res.data?.data as TitleMetadata;
        }
      })
      .catch((error) => {
        console.error(error);
        return null;
      })
      .finally(() => {
        setFetchingTitleMetadata(false);
      });
  };

  useEffect(() => {
    if (title?.id && title?.metadata_languages?.length > 0) {
      const language = getPriorityLanguage(title.metadata_languages, languagesPriority);
      setSelectedLanguage(language);
      fetchMetadata(title.id, language).then((metadata) => {
        setTitleMetadata(metadata);
      });
    }
  }, [title]);

  useEffect(() => {
    if (isActiveVersionAVersion(activeVersion) && activeVersion?.metadata_languages?.length > 0) {
      const language = getPriorityLanguage(activeVersion.metadata_languages, languagesPriority);
      setSelectedLanguage(language);
      fetchMetadata(activeVersion.id, language).then((metadata) => {
        setVersionMetadata(metadata);
      });
    }
  }, [activeVersion]);

  const selectLanguage = (language: string) => {
    setSelectedLanguage(language);
    if (isActiveVersionAVersion(activeVersion)) {
      fetchMetadata(activeVersion.id, language).then((metadata) => {
        setVersionMetadata(metadata);
      });
    }
    if (title?.id) {
      fetchMetadata(title.id, language).then((metadata) => {
        setTitleMetadata(metadata);
      });
    }
  };

  const languageOptions = (isActiveVersionAVersion(activeVersion) && activeVersion.metadata_languages) || title?.metadata_languages || [];
  return (
    <div style={{ position: 'relative' }}>
      {fetchingTitleMetadata && <Spinner />}
      {languageOptions?.length > 0 && (
        <LanguageDropdown style={{ top: disableDropdownOffset ? 0 : -70 }}>
          <DropdownSelect label="Language" value={selectedLanguage} onChange={selectLanguage} disabled={languageOptions?.length === 1}>
            {languageOptions?.map((lang: string) => (
              <MenuItem key={lang} value={lang} sx={{ display: 'flex', alignItems: 'center', fontSize: '1.2rem' }}>
                {/* <Box sx={{ fontSize: '1.6rem', mr: 1 }}>{FLAG_ICONS[lang.toLowerCase() as keyof typeof FLAG_ICONS]}</Box> */}
                <CSVLanguages codes={[lang]} type="base" />
              </MenuItem>
            ))}
          </DropdownSelect>
        </LanguageDropdown>
      )}
      <SizePreservingLoader isLoading={isLoading} loader={<SkeletonLoader />}>
        <Grid container columnSpacing={6} rowSpacing={3} sx={{ position: 'relative', opacity: fetchingTitleMetadata ? 0.2 : 1 }}>
          {titleMetadata ? (
            <Grid item sx={{ mt: disableDropdownOffset ? 4 : 0 }}>
              <SchemaForm schema={titleMetadata?.metadata_schema} formData={titleMetadata?.metadata_values} readonly />
            </Grid>
          ) : (
            //  CUSTOM CLIENT FIELDS FROM CONFIG
            !!customFields &&
            Object.keys(customFields).map((field) => {
              const shouldDisplayForType = !customFields[field].entity_type || customFields[field].entity_type?.includes(title?.entity_type);
              if (!shouldDisplayForType) return null;
              const fieldLocation = field.split('__').join('.');
              const value = get(title, fieldLocation);
              let label = customFields[field].label;
              label = label.replace(/{{(.+?)}}/g, title?.entity_type);
              label = label.substring(0, 1).toUpperCase() + label.substring(1);
              return (
                <LabelValue key={field} label={label}>
                  {customFields[field].render ? customFields[field].render(value, title, parentTitle) : value}
                </LabelValue>
              );
            })
          )}
        </Grid>
      </SizePreservingLoader>
      {isActiveVersionAVersion(activeVersion) && versionMetadata && <TitleDetailMetadataSchema />}
    </div>
  );
};

export default TitleDetailMetadata;

const SkeletonLoader = () => (
  <Grid container direction="column" rowSpacing={3} justifyContent="space-around">
    <Grid item>
      <Skeleton
        sx={{
          transform: 'unset' // by default skeleton is squeezed vertically (has scale(1, .6)), so by applying `transform: 'unset'` we cancel that default transformation.
        }}
      />
    </Grid>
    <Grid item>
      <Skeleton width="75%" sx={{ transform: 'unset' }} />
    </Grid>
  </Grid>
);

const LanguageDropdown = styled(Box)`
  display: inline-flex;
  align-items: center;
  position: absolute;
  z-index: 1;
  right: 0;

  .MuiFormControl-root {
    min-width: 200px;
  }

  .MuiSelect-select {
    display: flex;
    align-items: center;
    font-size: 1.2rem;
  }
`;
